import { useState, Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { Check, ChevronDown, Activity, TrendingUp, Zap, Clock, AlertCircle } from 'lucide-react'

interface Plan {
  id: 'NOT_ACTIVATED' | 'TRIAL' | 'STARTER' | 'GROWTH' | 'PRO'
  name: string
  description: string
  monthlyPrice: number
  icon: typeof Activity
  color: string
  bgColor: string
}

const PLANS: Plan[] = [
  {
    id: 'NOT_ACTIVATED',
    name: 'Not Activated',
    description: 'Select a plan to activate this brand',
    monthlyPrice: 0,
    icon: AlertCircle,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50'
  },
  {
    id: 'TRIAL',
    name: 'Trial (Starter)',
    description: '7-day free trial with Starter features',
    monthlyPrice: 0,
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50'
  },
  {
    id: 'STARTER',
    name: 'Starter',
    description: 'Monthly analysis - 10 queries, 3 competitors',
    monthlyPrice: 15,
    icon: Activity,
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  {
    id: 'GROWTH',
    name: 'Growth',
    description: 'Monthly analysis - 30 queries, 6 competitors, AI Insights',
    monthlyPrice: 29,
    icon: TrendingUp,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  {
    id: 'PRO',
    name: 'Pro',
    description: 'Weekly analysis - 75 queries, 9 competitors, all features',
    monthlyPrice: 69,
    icon: Zap,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  }
]

interface PlanComboboxProps {
  currentPlan: 'TRIAL' | 'STARTER' | 'GROWTH' | 'PRO' | null
  onPlanSelect: (plan: Plan) => void
  disabled?: boolean
}

export default function PlanCombobox({ currentPlan, onPlanSelect, disabled }: PlanComboboxProps) {
  // If currentPlan is null, show NOT_ACTIVATED, otherwise find the matching plan
  const getSelectedPlan = () => {
    if (currentPlan === null) {
      return PLANS.find(p => p.id === 'NOT_ACTIVATED')!
    }
    return PLANS.find(p => p.id === currentPlan) || PLANS.find(p => p.id === 'NOT_ACTIVATED')!
  }

  const [selected, setSelected] = useState<Plan>(getSelectedPlan())

  const handleChange = (plan: Plan) => {
    // Don't allow selecting NOT_ACTIVATED or TRIAL - they're display-only
    if (plan.id === 'NOT_ACTIVATED' || plan.id === 'TRIAL') return

    setSelected(plan)
    onPlanSelect(plan)
  }

  return (
    <Listbox value={selected} onChange={handleChange} disabled={disabled}>
      <div className="relative">
        <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white border-2 border-gray-200 py-3 pl-4 pr-10 text-left shadow-sm hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${selected.bgColor}`}>
              <selected.icon className={`h-5 w-5 ${selected.color}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="block text-sm font-semibold text-gray-900">
                  {selected.name}
                </span>
                {selected.monthlyPrice > 0 && (
                  <span className="text-sm font-bold text-gray-900">
                    ${selected.monthlyPrice}/mo
                  </span>
                )}
              </div>
              <span className="block text-xs text-gray-500 mt-0.5">
                {selected.description}
              </span>
            </div>
          </div>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ChevronDown
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 bottom-full mb-2 w-full overflow-auto rounded-lg bg-white shadow-2xl ring-2 ring-indigo-500 ring-opacity-50 focus:outline-none max-h-96">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-b border-indigo-600">
              <h3 className="text-sm font-bold uppercase tracking-wide">Choose Your Plan</h3>
              <p className="text-xs text-indigo-100 mt-0.5">Select a subscription tier for this brand</p>
            </div>

            {/* Plan Options */}
            <div className="py-2">
              {PLANS.filter(plan => plan.id !== 'TRIAL' && plan.id !== 'NOT_ACTIVATED').map((plan) => (
              <Listbox.Option
                key={plan.id}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-4 pl-4 pr-10 mx-2 my-1 rounded-lg transition-all ${
                    active ? 'bg-indigo-50 ring-2 ring-indigo-500 shadow-md transform scale-[1.02]' : 'bg-white hover:bg-gray-50'
                  }`
                }
                value={plan}
              >
                {({ selected: isSelected }) => (
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${plan.bgColor}`}>
                      <plan.icon className={`h-5 w-5 ${plan.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={`block text-sm font-semibold ${isSelected ? 'text-indigo-600' : 'text-gray-900'}`}>
                          {plan.name}
                        </span>
                        {plan.monthlyPrice > 0 && (
                          <span className={`text-sm font-bold ${isSelected ? 'text-indigo-600' : 'text-gray-900'}`}>
                            ${plan.monthlyPrice}/mo
                          </span>
                        )}
                      </div>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {plan.description}
                      </span>
                    </div>
                    {isSelected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <Check className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                )}
              </Listbox.Option>
              ))}
            </div>
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}
